import { isEmpty } from "lodash";
import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import moment from "moment";
import { getDir } from "orion-components/i18n/Config/selector";
import { alertService } from "client-app-core";
import Alerts from "./Alerts/Alert";
import EventBus from "orion-components/SharedComponents/EventBus";
import { publishRecordedSeek } from "./playbackCommands";

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

const formattedTime = (time) => {
	return moment(time).format("HH:mm:ss");
};

const TimeLineControls = ({
	cameraId,
	instanceId,
	recordedMode,
	startTime,
	currentTime,
	videoDurationInMinutes,
	videoScaleInMinutes,
	isPlaying,
	setCurrentTime
}) => {
	const dir = useSelector((state) => getDir(state));

	const [timeLineControlLines, setTimeLineControlLines] = useState(0);
	const [linesPadding, setLinesPadding] = useState(20);
	const [resizeCount, setResizeCount] = useState(0);

	const [displayAlerts, setDisplayAlerts] = useState(true);
	const [alerts, setAlerts] = useState([]);
	const [alertsInFovRangeAndDateTime, setAlertsInFovRangeAndDateTime] = useState({});

	const [loadedDuration, setLoadedDuration] = useState(0); // seconds from startTime
	const [loadedBarWidth, setLoadedBarWidth] = useState(0);

	const alertsFetchedRef = useRef(false);

	const timeLineRef = useRef(null);
	const timeLineWrapperRef = useRef(null);
	const linesDistanceRef = useRef(null);

	const draggingStateRef = useRef({ dragging: false });

	const maxTime = moment(startTime).add(videoDurationInMinutes, "m").toDate();

	const toggleAlertVisiblity = (data) => {
		if (data.instanceId === instanceId) {
			setDisplayAlerts(data.value);
		}
	};

	const calculateLineDistance = () => {
		let distance = 0;
		const line1 = document.getElementById(`${instanceId}_vertical-line1`);
		const line2 = document.getElementById(`${instanceId}_vertical-line2`);

		if (line1 && line2) {
			const rect1 = line1.getBoundingClientRect();
			const rect2 = line2.getBoundingClientRect();

			distance = Math.abs(rect2.left - rect1.left);
		} else {
			return false;
		}
		linesDistanceRef.current = distance;
		return true;
	};

	useEffect(() => {
		EventBus.subscribe("toggleAlertVisiblityOnTimeLine", toggleAlertVisiblity);
		EventBus.subscribe("setCurrentRecordedTimestamp", setCurrentTimestamp);
		EventBus.subscribe("loadedDurationChange", loadedDurationChange);

		const resizeObserver = new ResizeObserver(() => setResizeCount((rc) => rc + 1));

		const seekbarElem = document.getElementById(`${instanceId}_seekbar`);
		if (seekbarElem) {
			resizeObserver.observe(seekbarElem);
		}

		return () => {
			EventBus.unsubscribe("toggleAlertVisiblityOnTimeLine", toggleAlertVisiblity);
			EventBus.unsubscribe("setCurrentRecordedTimestamp", setCurrentTimestamp);
			EventBus.unsubscribe("loadedDurationChange", loadedDurationChange);

			if (seekbarElem) {
				resizeObserver.unobserve(seekbarElem);
			}
		};
	}, []);

	useEffect(() => {
		if (!videoScaleInMinutes) {
			return;
		}

		const seekbarWidth = document.getElementById(`${instanceId}_seekbar`).getBoundingClientRect().width;
		const intervalCount = Math.floor(seekbarWidth / 21); // default 20 px padding and 1 px line width
		const padding = seekbarWidth / intervalCount - 1;
		const durationPerIntervalInMinutes = videoScaleInMinutes / intervalCount;
		const totalIntervals = Math.floor(videoDurationInMinutes / durationPerIntervalInMinutes);
		setTimeLineControlLines(totalIntervals + 1); // +1 for the first line
		setLinesPadding(padding);
		linesDistanceRef.current = null; // This would now need to be setup fresh after the new timeline gets rendered
	}, [startTime, videoDurationInMinutes, videoScaleInMinutes, resizeCount]);

	const setCurrentTimestamp = (data) => {
		if (data.instanceId !== instanceId) {
			return;
		}
		const time = data.time;
		setCurrentTime((prevTime) => {
			if (Math.abs(prevTime - time) < 1000) {
				// Difference less than a second, we ignore.
				return prevTime;
			} else {
				return time;
			}
		});
	};

	const loadedDurationChange = (data) => {
		if (data.instanceId !== instanceId) {
			return;
		}
		setLoadedDuration(data.duration);
	};

	useEffect(() => {
		let currTime = currentTime;
		if (currTime === null) {
			// We default to the start time if the playback has not yet started.
			currTime = startTime;
		}

		// Update progress marker's position on the timeline
		if (!linesDistanceRef.current) {
			if (!calculateLineDistance()) {
				return;
			}
		}

		const totalDistance = (timeLineControlLines - 1) * linesDistanceRef.current;
		const pixelsPerSecond = totalDistance / (videoDurationInMinutes * 60);
		const elapsedTimeInSeconds = (currTime - startTime) / 1000;
		const elapsedDistance = elapsedTimeInSeconds * pixelsPerSecond;

		if (dir === "ltr") {
			timeLineRef.current.style.marginLeft = `calc(50% - ${elapsedDistance}px`;
		} else {
			timeLineRef.current.style.marginRight = `calc(50% - ${elapsedDistance}px`;
		}
	}, [currentTime, startTime, videoDurationInMinutes, timeLineControlLines]);

	useEffect(() => {
		if (!displayAlerts || alertsFetchedRef.current) {
			return;
		}

		const params = {
			entityId: cameraId,
			entityType: "camera",
			startDateTime: startTime,
			endDateTime: maxTime
		};
		alertService.getAlertsByContextAndRange(params, (err, response) => {
			if (err) {
				console.log("ERROR", err);
			}
			if (response?.success === false) {
				return;
			}
			if (response) {
				setAlerts(response);
				alertsFetchedRef.current = true;
			}
		});
	}, [cameraId, startTime, videoDurationInMinutes, displayAlerts]);

	const getVerticalLineForAlertPlacement = (alertCreatedDateTime) => {
		// Convert the datetimes to timestamps (milliseconds since epoch)
		const start = startTime.getTime();
		const end = maxTime.getTime();
		const between = new Date(alertCreatedDateTime).getTime();

		// Check if the alertCreatedDateTime is within the range of startDateTime and endDateTime
		if (between < start || between > end) {
			// The alert created date time must be within the start and end date times
			return;
		}

		// Calculate the total duration and the duration from start to the between datetime
		const totalDuration = end - start;
		const betweenDuration = between - start;

		// Calculate the position as a percentage of the total duration
		const positionPercentage = (betweenDuration / totalDuration) * 100;

		// Calculate the vertical line based on the number of total control lines
		const verticalLinePosition = Math.round((positionPercentage / 100) * timeLineControlLines);

		return verticalLinePosition;
	};

	useEffect(() => {
		/*// sample alerts for testing purpose
		setAlertsInFovRangeAndDateTime({
			0: {
				verticalLineNumber: 0,
				createdDate: "2024-07-21T08:41:38.888Z",
				id: "0c240ed4-5d84-4712-941f-3b68152c0457",
				name: "Alert 97"
			},
			2: {
				verticalLineNumber: 2,
				createdDate: "2024-07-21T08:41:38.888Z",
				id: "0c240ed4-5d84-4712-941f-3b68152c0457",
				name: "Alert 97"
			}
		});
		return;*/

		if (displayAlerts && !isEmpty(alerts)) {
			const formattedData = {};
			alerts.forEach((element) => {
				const key = getVerticalLineForAlertPlacement(element.createdDate);
				const value = {
					id: element.id,
					name: element.message || "",
					createdDate: element.createdDate,
					verticalLineNumber: key
				};
				formattedData[key] = value;
			});
			setAlertsInFovRangeAndDateTime(formattedData);
		}
	}, [displayAlerts, alerts, timeLineControlLines]);

	const initiateDragging = (e) => {
		if (recordedMode !== "recorded" || currentTime === null) {
			// We allow dragging only when paused in recorded mode
			return;
		}

		if (!linesDistanceRef.current) {
			if (!calculateLineDistance()) {
				return;
			}
		}

		const totalDistance = (timeLineControlLines - 1) * linesDistanceRef.current;
		const secondsPerPixel = (videoDurationInMinutes * 60) / totalDistance;

		e.preventDefault();
		e.stopPropagation();
		e.target.setPointerCapture(e.pointerId || 1);

		draggingStateRef.current = {
			dragging: true,
			initialX: e.clientX,
			initialTime: currentTime,
			secondsPerPixel
		};
	};

	const endDragging = (e) => {
		draggingStateRef.current = { dragging: false };
		if (e.target.releasePointerCapture) {
			e.target.releasePointerCapture(e.pointerId || 1);
		}
	};

	const handleDragMove = (e) => {
		if (draggingStateRef.current.dragging) {
			const draggingState = draggingStateRef.current;
			const deltaX = draggingState.initialX - e.clientX;
			const deltaTime = deltaX * draggingState.secondsPerPixel;
			let newTime = moment(draggingState.initialTime).add(deltaTime, "s").toDate();
			if (newTime < startTime) {
				newTime = startTime;
			} else if (newTime > maxTime) {
				newTime = maxTime;
			}
			
			publishRecordedSeek({ instanceId, time: newTime });
		}
	};

	useEffect(() => {
		if (linesDistanceRef.current && timeLineControlLines > 1 && videoDurationInMinutes > 0) {
			const percent = Math.max(0, Math.min(1, loadedDuration / (videoDurationInMinutes * 60)));
			const width = percent * ((timeLineControlLines - 1) * linesDistanceRef.current);
			setLoadedBarWidth(width);
		} else {
			setLoadedBarWidth(0);
		}
	}, [loadedDuration, videoDurationInMinutes, timeLineControlLines, linesDistanceRef.current]);

	return (
		<div
			style={{
				display: "flex",
				alignItems: "center",
				position: "relative",
				width: "100%",
				height: "100%",
				background: "transparent"
			}}
		>
			<div
				style={{
					position: "absolute",
					backgroundColor: "#607698A3",
					width: 36.77,
					height: 32,
					fontSize: 9.5,
					left: "calc(50% - 16.8px)",
					pointerEvents: "none"
				}}
			>
				<div
					style={{
						textAlign: "center",
						color: "#fff",
						fontSize: "1em",
						width: "100%",
						height: 14,
						lineHeight: 1,
						overflow: "hidden"
					}}
				>
					<span>{currentTime !== null ? formattedTime(currentTime) : ""}</span>
				</div>
				<div
					id={`${instanceId}_progress-marker`}
					style={{
						...(dir === "ltr" && { marginLeft: "44%", borderLeft: "1px solid #4BA0D6" }),
						...(dir === "rtl" && { marginRight: "44%", borderRight: "1px solid #4BA0D6" }),
						marginTop: "-12%",
						height: 20
					}}
				></div>
			</div>
			<div
				style={{ width: "100%", overflowX: "hidden", position: "relative" }}
				id={`${instanceId}_seekbar`}
				onMouseDown={initiateDragging}
				onMouseMove={handleDragMove}
				onMouseUp={() => (draggingStateRef.current = { dragging: false })}
				onMouseLeave={endDragging} data-testid="orion-components-cbcomponents-new-video-player-components-recorded-time-line-controls-div"
			>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						position: "relative"
					}}
					ref={timeLineRef}
				>
					{[...Array(timeLineControlLines)].map((_, index) => (
						<div
							ref={timeLineWrapperRef}
							key={`${timeLineControlLines.length}_${index}`}
							style={{ paddingTop: "5px", paddingRight: linesPadding }}
							className="vertical-line-wrapper" data-testid="orion-components-cbcomponents-new-video-player-components-recorded-time-line-controls-div-2"
						>
							<div style={{ display: "flex" }}>
								<div
									style={{
										zIndex: 1,
										...(dir === "ltr" && { marginRight: "-60%" }),
										...(dir === "rtl" && { marginLeft: "-60%" }),
										marginTop: index % 4 === 0 ? "2px" : "0px"
									}}
								>
									{displayAlerts && hasOwn(alertsInFovRangeAndDateTime, index) ? (
										<Alerts alertData={alertsInFovRangeAndDateTime[index]} />
									) : null}
								</div>
								<div
									data-name="vertical-lines"
									id={`${instanceId}_vertical-line${index + 1}`}
									style={{
										height: index % 4 === 0 ? "20px" : "10px",
										...(dir === "ltr" && {
											borderLeft: `1px solid #808080`
										}),
										...(dir === "rtl" && {
											borderRight: `1px solid #808080`
										}),
										flex: 1,
										marginTop:
											hasOwn(alertsInFovRangeAndDateTime, index) && index % 4 !== 0
												? "5px"
												: "0px"
									}}
								/>
							</div>
						</div>
					))}
					{/* Loaded duration bar */}
					<div
						style={{
							position: "absolute",
							bottom: "0px",
							left: 0,
							height: "20px",
							background: "#4BA0D6",
							borderRadius: "2px",
							opacity: 0.25,
							zIndex: 0,
							width: `${loadedBarWidth}px`,
							transition: "width 0.2s"
						}}
					/>
				</div>
			</div>
		</div>
	);
};

TimeLineControls.propTypes = {
	cameraId: PropTypes.string,
	instanceId: PropTypes.string,
	recordedMode: PropTypes.string,
	startTime: PropTypes.instanceOf(Date),
	currentTime: PropTypes.instanceOf(Date),
	videoDurationInMinutes: PropTypes.number,
	videoScaleInMinutes: PropTypes.number,
	isPlaying: PropTypes.bool,
	setCurrentTime: PropTypes.func.isRequired
};

export default TimeLineControls;
